<template>
  <div class="card-orcamento" @click="onClick">
    <div class="card-orcamento__header">
      <span class="card-orcamento__id">#{{ orcamento.idOrcamento }}</span>
      <span
        v-if="orcamento.origem === 'VAR'"
        class="card-orcamento__badge"
        v-title="'Gerado automaticamente pela varredura de renovação'"
      >
        <i class="pi pi-refresh" />
      </span>
    </div>
    <div class="card-orcamento__title" v-text="getTituloTransportador()" />
    <div class="card-orcamento__meta">
      <span v-text="qtdItensLabel" />
      <span
        class="card-orcamento__valor"
        v-text="formatMoney(orcamento.valorTotal)"
      />
    </div>
    <div
      v-if="orcamento.estagio === 'ENV' && diasDesdeEnvio !== null"
      class="card-orcamento__envio"
      :class="{ 'card-orcamento__envio--alerta': diasDesdeEnvio >= 3 }"
    >
      <i class="pi pi-clock" /> {{ diasDesdeEnvio }} dia(s) desde envio
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, PropType } from "vue";
import { OrcamentoPayload } from "../../modules/business/orcamento/orcamentoBusiness";

export default defineComponent({
  setup(props, { emit }) {
    const getTituloTransportador = () => {
      const o = props.orcamento;
      if (o.nomeTransportador) {
        return o.nomeTransportador;
      }
      return o.idTransportador
        ? `Transportador #${o.idTransportador}`
        : "Sem transportador";
    };

    const qtdItensLabel = computed(() => {
      const n = props.orcamento.qtdItens ?? props.orcamento.itens?.length ?? 0;
      return n === 1 ? "1 item" : `${n} itens`;
    });

    const formatMoney = (valor?: number | null) => {
      if (valor === null || valor === undefined) return "R$ 0,00";
      return valor.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL"
      });
    };

    const diasDesdeEnvio = computed<number | null>(() => {
      const dataEnvio = props.orcamento.dataEnvio;
      if (!dataEnvio) return null;
      const d = typeof dataEnvio === "string" ? new Date(dataEnvio) : dataEnvio;
      const diff = Date.now() - d.getTime();
      return Math.floor(diff / (1000 * 60 * 60 * 24));
    });

    const onClick = () => {
      emit("click-orcamento", props.orcamento);
    };

    return {
      getTituloTransportador,
      qtdItensLabel,
      formatMoney,
      diasDesdeEnvio,
      onClick
    };
  },
  props: {
    orcamento: {
      type: Object as PropType<OrcamentoPayload>,
      required: true
    }
  }
});
</script>

<style scoped lang="scss">
.card-orcamento {
  background: #fff;
  border-radius: 6px;
  padding: 0.75rem;
  margin-bottom: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  cursor: grab;
  transition: box-shadow 0.2s, transform 0.1s;
  border-left: 4px solid #1976d2;

  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.18);
    transform: translateY(-1px);
  }

  &:active {
    cursor: grabbing;
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.25rem;
  }

  &__id {
    font-size: 0.75rem;
    color: #757575;
    font-weight: 600;
  }

  &__badge {
    color: #1976d2;
    font-size: 0.75rem;
  }

  &__title {
    font-weight: 600;
    margin-bottom: 0.25rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__meta {
    display: flex;
    justify-content: space-between;
    font-size: 0.875rem;
    color: #424242;
  }

  &__valor {
    font-weight: 600;
    color: #2e7d32;
  }

  &__envio {
    margin-top: 0.5rem;
    font-size: 0.75rem;
    color: #757575;

    &--alerta {
      color: #d84315;
      font-weight: 600;
    }
  }
}
</style>
